import React, { useState, useEffect, useCallback } from 'react';
import { Share2, Printer, ArrowLeft, ArrowRight, X } from 'lucide-react';
import { PLACEHOLDER_IMAGE, getWatermarkedImageUrl } from '../../utils/imageProxy';
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Counter from "yet-another-react-lightbox/plugins/counter";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/plugins/counter.css";

interface PropertyGalleryProps {
  property: any;
  onShareClick?: () => void;
  onPrintClick?: () => void;
}

const PropertyGallery: React.FC<PropertyGalleryProps> = ({ property, onShareClick, onPrintClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Get property images in the format expected by the lightbox
  const getPropertyImages = useCallback(() => {
    if (!property.property_images?.length) {
      return [{
        src: property.primaryImage || PLACEHOLDER_IMAGE,
        alt: 'Immagine non disponibile',
      }];
    }

    // Sort the images: primary first, then by position
    const sortedImages = [...property.property_images].sort((a, b) => {
      if (a.is_primary && !b.is_primary) return -1;
      if (!a.is_primary && b.is_primary) return 1;
      return (a.position || 0) - (b.position || 0);
    });

    // Il lightbox è a schermo intero e permette lo zoom: serve la risoluzione
    // più alta che la funzione accetta.
    return sortedImages.map((img: any) => ({
      src: getWatermarkedImageUrl(img.url, 2000),
      alt: property.title || 'Immagine immobile',
      loading: 'lazy' as const,
    }));
  }, [property]);

  const images = getPropertyImages();

  const renderImageGrid = () => {
    return (
      <div className="flex flex-col h-full gap-2">
        {/* Main Image */}
        <div className="relative w-full h-[300px] md:h-[400px]">
          <div
            className="w-full h-full cursor-pointer overflow-hidden rounded-lg"
            onClick={() => { setOpen(true); setCurrentIndex(0) }}
          >
            <img
              src={images[0]?.src}
              alt={images[0]?.alt}
              className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
              loading="lazy"
            />
          </div>

          {/* Action buttons */}
          <div className="absolute top-4 right-4 flex space-x-2 z-10">
            {onShareClick && (
              <button
                onClick={(e) => { e.stopPropagation(); onShareClick(); }}
                className="w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-sm"
                aria-label="Condividi immobile"
              >
                <Share2 className="h-5 w-5 text-gray-700" />
              </button>
            )}
            {onPrintClick && (
              <button
                onClick={(e) => { e.stopPropagation(); onPrintClick(); }}
                className="w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-sm"
                aria-label="Stampa scheda immobile"
              >
                <Printer className="h-5 w-5 text-gray-700" />
              </button>
            )}
          </div>

          {/* "Visualizza tutte le foto" button */}
          <button
            onClick={() => { setOpen(true) }}
            className="absolute bottom-4 right-4 bg-white/90 hover:bg-white text-primary px-4 py-2 rounded-lg shadow-md transition-colors backdrop-blur-sm text-sm font-medium"
          >
            Visualizza tutte le foto ({images.length})
          </button>
        </div>

        {/* Thumbnails Strip */}
        {images.length > 1 && (
          <div className="relative w-full h-[100px] mt-1">
            <div className="flex gap-2 overflow-x-auto pb-2 h-full scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
              {images.slice(1).map((img: any, idx: number) => ( // Show all images except the first one in the strip
                <div
                  key={idx + 1}
                  className="relative min-w-[140px] md:min-w-[calc(25%-6px)] h-full cursor-pointer flex-shrink-0"
                  onClick={() => {
                    setOpen(true);
                    setCurrentIndex(idx + 1);
                  }}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover rounded-lg hover:opacity-90 transition-opacity"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="relative h-auto rounded-lg">
      {/* Main gallery grid display */}
      {renderImageGrid()}

      {/* Lightbox */}
      <Lightbox
        open={open}
        close={() => setOpen(false)}
        slides={images}
        index={currentIndex}
        plugins={isMobile ? [Zoom, Counter] : [Thumbnails, Zoom, Counter]}
        controller={{ closeOnBackdropClick: true }}
        carousel={{
          finite: images.length <= 1,
          preload: 3
        }}
        render={{
          iconPrev: () => <ArrowLeft className="w-6 h-6" />,
          iconNext: () => <ArrowRight className="w-6 h-6" />,
          iconClose: () => <X className="w-6 h-6" />,
        }}
        animation={{ fade: 300 }}
        thumbnails={!isMobile ? {
          position: "bottom",
          width: 120,
          height: 80,
          border: 2,
          borderRadius: 4,
          padding: 4,
          gap: 16,
          imageFit: "cover"
        } : undefined}
        styles={{
          root: {
            "--yarl__color_backdrop": "rgba(0, 0, 0, 0.9)",
          },
          container: {
            backgroundColor: "transparent",
            backdropFilter: "blur(8px)"
          },
          thumbnail: {
            border: "2px solid transparent",
            overflow: "hidden",
            borderRadius: "4px"
          },
          thumbnailsTrack: {
            display: "flex",
            justifyContent: "center"
          },
          navigationPrev: {
            background: "rgba(0, 0, 0, 0.5)",
            borderRadius: "50%",
            width: "44px",
            height: "44px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(4px)"
          },
          navigationNext: {
            background: "rgba(0, 0, 0, 0.5)",
            borderRadius: "50%",
            width: "44px",
            height: "44px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(4px)"
          }
        }}
        zoom={{
          maxZoomPixelRatio: 3,
          zoomInMultiplier: 2,
          doubleTapDelay: 300,
          doubleClickDelay: 500,
          keyboardMoveDistance: 50
        }}
      />
    </div>
  );
};

export default PropertyGallery;